import config from 'config'

import { AbstractService, LoggerParameter } from 'shared/services/AbstractService'
import { ValidationError } from 'shared/services/errors'
import type { DesignPersonalizationContext } from '@vp/personalization-types'
import { getLogger } from 'shared/logger'
import type { GalleryConfigIServiceConfig } from 'shared/types/gallery/config'
import type { DesignPersonalization } from 'shared/types/vp/designpersonalization'

const DEFAULT_DAYS_OF_HISTORY = 30

export class DesignPersonalizationContextServiceApi
  extends AbstractService
  implements DesignPersonalization.Services.DesignPersonalizationContextService.IDesignPersonalizationContextService
{
  apiVersion: number

  constructor({ version, ...rest }: GalleryConfigIServiceConfig, logger: LoggerParameter) {
    super(rest, logger)
    this.apiVersion = version
  }

  /**
   * Add interactions to DPCS
   * @param canonicalId
   * @param interactions
   * @param authorizationHeader
   */
  public async addTemplateInteractions(
    canonicalId: string,
    interactions: DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteraction[],
    authorizationHeader?: string,
  ): Promise<string> {
    const url = `${this.apiVersion}/templateInteractions:list`

    if (canonicalId === undefined || authorizationHeader === undefined) {
      throw new ValidationError('Unable to call addTemplateInteractions, no canonicalId and/or authorizationHeader provided', url, { canonicalId })
    }

    const { data } = await this.api.post<string>(
      url,
      { interactions },
      {
        headers: {
          Authorization: authorizationHeader,
        },
        params: {
          canonicalId,
          from: config.appName,
          requestor: config.appName,
        },
      },
    )

    return data
  }

  /**
   * Gets all interactions from DPCS for a given parameters
   * @param canonicalId
   * @param authorizationHeader
   * @param interactionTypes - optional array of interaction types
   * @param interactionSubtypes - optional array of interaction subtypes
   * @param daysOfHistory - optional number of days of history to retrieve
   */
  public async getTemplateInteractions(
    canonicalId: string | null,
    authorizationHeader: string | undefined,
    interactionTypes?: string[],
    interactionSubtypes?: string[],
    daysOfHistory = DEFAULT_DAYS_OF_HISTORY.toString(),
  ): Promise<DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteraction[]> {
    const url = `${this.apiVersion}/templateInteractions`

    if (!authorizationHeader) {
      throw new ValidationError('Unable to call getTemplateInteractions, no authorizationHeader provided', url)
    }

    try {
      const { data } = await this.api.get<DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteraction[]>(url, {
        headers: {
          Authorization: authorizationHeader,
        },
        params: {
          canonicalId,
          daysOfHistory,
          interactionSubtype: interactionSubtypes,
          interactionType: interactionTypes,
          requestor: config.appName,
        },
        paramsSerializer: { arrayFormat: 'repeat' },
      })

      return data
    } catch (e) {
      // Log error but don't throw
      this.logger.error(`Failed to get template interactions: `, e)
      // Return empty array instead of throwing
      return []
    }
  }

  /**
   * Gets summary of interactions from DPCS for a given parameters
   * @param canonicalId
   * @param interactionTypes
   * @param interactionSubtypes
   * @param productKey
   * @param daysOfHistory
   * @param authorizationHeader
   */
  public async getTemplateInteractionsSummary(
    canonicalId: string,
    interactionTypes?: string[],
    interactionSubtypes?: string[],
    productKey?: string[],
    daysOfHistory?: number,
    authorizationHeader?: string | null,
  ): Promise<DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteractionSummary[]> {
    const url = `${this.apiVersion}/templateInteractions/summary`

    if (!canonicalId || !authorizationHeader) {
      throw new ValidationError('Unable to call getTemplateInteractionsSummary, no canonicalId and/or authorizationHeader provided', url)
    }

    const { data } = await this.api.get<DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteractionSummary[]>(url, {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        canonicalId,
        daysOfHistory,
        requestor: config.appName,
        interactionSubtype: interactionSubtypes,
        interactionType: interactionTypes,
        productKey,
      },
    })

    return data
  }

  /**
   * Gets the latest design personalization context for the session
   * @param sessionId
   * @param canonicalId
   * @param authorizationHeader
   */
  public async getDesignPersonalizationSession(
    sessionId: string,
    authorizationHeader?: string,
    canonicalId?: string,
  ): Promise<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse> {
    const url = `${this.apiVersion}/designPersonalizationSessions/${sessionId}`

    if (!authorizationHeader) {
      throw new ValidationError('Unable to call getDesignPersonalizationSession, no authorizationHeader provided', url)
    }

    const { data } = await this.api.get<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse>(url, {
      headers: {
        Authorization: authorizationHeader,
        From: config.appName,
      },
      params: {
        canonicalId,
      },
    })

    return data
  }

  /**
   * Create a new design personalization session with the design personalization data/context
   * @param designPersonalizationData
   * @param brandId
   * @param canonicalId
   * @param authorizationHeader
   */
  public async createDesignPersonalizationSession(
    designPersonalizationData: DesignPersonalizationContext,
    brandId?: string,
    authorizationHeader?: string,
    canonicalId?: string,
  ): Promise<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse> {
    const url = `${this.apiVersion}/designPersonalizationSessions`

    if (canonicalId === undefined && authorizationHeader === undefined) {
      throw new ValidationError('Unable to call createDesignPersonalizationSession, no canonicalId and/or authorizationHeader provided', url, { canonicalId })
    }

    const body: DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSession = {
      brandId,
      designPersonalizationContext: {
        designPersonalizationData,
      },
    }

    const { data } = await this.api.post<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse>(
      url,
      { ...body },
      {
        headers: {
          Authorization: authorizationHeader,
          From: config.appName,
        },
        params: {
          canonicalId,
        },
      },
    )

    return data
  }

  /**
   * Update an existing design personalization session with the design personalization data/context
   * @param sessionId
   * @param designPersonalizationData
   * @param brandId
   * @param canonicalId
   * @param authorizationHeader
   */
  public async updateDesignPersonalizationSession(
    sessionId: string,
    designPersonalizationData: DesignPersonalizationContext,
    brandId?: string,
    authorizationHeader?: string,
    canonicalId?: string,
  ): Promise<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse> {
    const url = `${this.apiVersion}/designPersonalizationSessions/${sessionId}/designPersonalizationContexts`

    if (canonicalId === undefined && authorizationHeader === undefined) {
      throw new ValidationError('Unable to call updateDesignPersonalizationSession, no canonicalId and/or authorizationHeader provided', url, { canonicalId })
    }

    const body: DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSession = {
      brandId,
      designPersonalizationContext: {
        designPersonalizationData,
      },
    }

    const { data } = await this.api.post<DesignPersonalization.Models.DesignPersonalizationContextService.PersonalizationSessionResponse>(
      url,
      { ...body },
      {
        headers: {
          Authorization: authorizationHeader,
          From: config.appName,
        },
        params: {
          canonicalId,
        },
      },
    )

    return data
  }
}

export const DesignPersonalizationContextService = new DesignPersonalizationContextServiceApi(config.services.designPersonalizationContextService, getLogger)
